import "./style.scss";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { setData, setVisibleColumns } from "@/src/store/slices/OfficeAccountingPage/officeAccountingTableSlice";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useFilteredObject } from "@/src/hooks/useFilteredObject";
import { normalizeApplicantAccountingListResponse } from "@/src/utils/officeAccountingMappers";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { normalizeFormOfficesForApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import { IOfficeAccountingTablePayload } from "@/src/types/officeAccounting";
import { IFiltersList, IRowData } from "@/src/types/applicationOnline";
import CustomFullNamePopUp from "@/src/components/CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import OAPHeader from "./OAPHeader/OAPHeader";
import OAPBody from "./OAPBody/OAPBody";
import OAPFooter from "./OAPFooter/OAPFooter";
import { OpenOfficeAccountingFullNameParams } from "./openOfficeAccountingFullNamePopUp";
import { filterRowsByAccess, resolveAccessFilteredCount } from "@/src/utils/accessControl/filterByAccess";
import { resolveCountryAccessFromRole } from "@/src/utils/accessControl/resolveCountryAccessFromRole";

const getSearchQuery = (filterResults: Record<string, string[] | string | null>): string => {
  const query = filterResults.query;
  return typeof query === "string" ? query.trim() : "";
};

const getCampusOfficeFilter = (filterResults: Record<string, string[] | string | null>): string[] => {
  const campus = filterResults.campus;
  if (!Array.isArray(campus) || campus.length === 0) return [];
  return normalizeFormOfficesForApi(campus);
};

/**
 * `office` query for GET /applicant-accounting/list:
 * - Campus column filter (multi) takes priority → `office=POLAND&office=UKRAINE`
 * - Else header select: All → omit `office`; single office → `office=MALTA`
 */
const resolveOfficeAccountingOfficeQuery = (
  actualOfficeTable: string,
  filterResults: Record<string, string[] | string | null>,
): string[] => {
  const campusOffices = getCampusOfficeFilter(filterResults);
  if (campusOffices.length > 0) return campusOffices;
  return normalizeFormOfficesForApi(actualOfficeTable);
};

const EMPTY_FILTERS_LIST = {} as IFiltersList;

const OAPLayout = () => {
  const dispatch = useDispatch();
  const visibleColumns = useSelector((state: RootState) => state.officeAccountingTable.visibleColumns);
  const actualOfficeTable = useSelector((state: RootState) => state.officeAccountingFilters.actualOfficeTable);
  const bodyFilters = useSelector((state: RootState) => state.officeAccountingFilters.filterResults);
  const actualFilterStatus = useSelector((state: RootState) => state.officeAccountingFilters.filterStatus);
  const selectedSiteUrl = useSelector((state: RootState) => state.accessControl.selectedSiteUrl);
  const userRole = useSelector((state: RootState) => state.accessControl.userRole);

  const [isCurrentPage, setIsCurrentPage] = useState("1");
  const [isSelectPagination, setIsSelectPagination] = useState("5");
  const [actualQuantityRows, setActualQuantityRows] = useState(0);
  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [isApPopUpFullName, setIsApPopUpFullName] = useState(false);
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentAccountingRowId, setCurrentAccountingRowId] = useState<number | undefined>();
  const [popUpInitialTabId, setPopUpInitialTabId] = useState("general-information");

  const bodyFilterFetch = useFilteredObject(bodyFilters);
  const searchQuery = getSearchQuery(bodyFilterFetch as Record<string, string[] | string | null>);
  const officeQuery = resolveOfficeAccountingOfficeQuery(
    actualOfficeTable,
    bodyFilterFetch as Record<string, string[] | string | null>,
  );

  const loadActualForm = useCallback(async () => {
    dispatch(setIsPreloyder(true));
    try {
      const offset = Number(isSelectPagination) * (Number(isCurrentPage) - 1);
      const officeUrl = selectedSiteUrl.trim() || undefined;

      const response = await officeAccountingPageRequests.getApplicantAccountingList(
        isSelectPagination,
        offset,
        officeQuery,
        officeUrl,
      );

      if (response) {
        const normalized = normalizeApplicantAccountingListResponse(response);
        const roleAccess = resolveCountryAccessFromRole(userRole);
        const filteredData = filterRowsByAccess(normalized.data, {
          officeUrlQuery: officeUrl,
          officeQuery: officeQuery.length === 1 ? officeQuery[0] : undefined,
          roleHasFullCountryAccess: roleAccess.hasFullCountryAccess,
          roleAllowedOffices: roleAccess.allowedOffices,
        });
        const tablePayload: IOfficeAccountingTablePayload = {
          data: filteredData,
          count_form: resolveAccessFilteredCount(
            filteredData.length,
            normalized.data.length,
            normalized.count_form,
          ),
          filters_list: EMPTY_FILTERS_LIST,
        };
        console.groupCollapsed(
          `[OfficeAccounting] GET /applicant-accounting/list (office=${officeQuery.join(",") || "null"})`,
        );
        console.log("API raw response:", response);
        console.log("Mapped table rows:", filteredData);
        console.log("Row count:", tablePayload.count_form);
        console.groupEnd();
        dispatch(setData(tablePayload));
        setActualQuantityRows(tablePayload.count_form);
      }
    } catch (error) {
      console.error("Office Accounting load error:", error);
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [
    dispatch,
    isSelectPagination,
    isCurrentPage,
    officeQuery,
    selectedSiteUrl,
    userRole,
  ]);

  useEffect(() => {
    loadActualForm();
  }, [loadActualForm]);

  useEffect(() => {
    if (!visibleColumns.some((col) => col.id === "id")) return;
    dispatch(setVisibleColumns(visibleColumns));
  }, [dispatch, visibleColumns]);

  useEffect(() => {
    setIsCurrentPage("1");
  }, [actualFilterStatus, actualOfficeTable, searchQuery, selectedSiteUrl, bodyFilters.campus]);

  const refreshCurrentStudentUserInfo = useCallback(async (): Promise<IRowData["userInfo"] | undefined> => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (userInfo) setCurrentDataPopUp(userInfo);
      return userInfo;
    } catch (error) {
      console.log("refreshCurrentStudentUserInfo error:", error);
      return undefined;
    }
  }, [currentStudentId]);

  const handleOpenFullNamePopUp = useCallback(async ({ applicantId, studentInfoId, initialTabId }: OpenOfficeAccountingFullNameParams) => {
    setCurrentStudentId(applicantId);
    setCurrentAccountingRowId(studentInfoId);
    setPopUpInitialTabId(initialTabId ?? "general-information");
    try {
      const userInfo = await fetchApplicantUserInfo(applicantId);
      if (userInfo) setCurrentDataPopUp(userInfo);
    } catch (error) {
      console.log("Office Accounting full name popup error:", error);
    }
    setIsApPopUpFullName(true);
  }, []);

  return (
    <div className="container office-accounting">
      <CustomFullNamePopUp
        isApPopUpFullName={isApPopUpFullName}
        setIsApPopUpFullName={setIsApPopUpFullName}
        currentDataPopUp={currentDataPopUp}
        currentStudentId={currentStudentId}
        currentApplyInfoId={currentAccountingRowId}
        tableContext="office-accounting"
        initialTabId={popUpInitialTabId}
        onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
        onRefreshTableData={loadActualForm}
      />
      <OAPHeader
        loadActualForm={loadActualForm}
        isShowSettingsModal={isShowSettingsModal}
        setIsShowSettingsModal={setIsShowSettingsModal}
      />
      <OAPBody
        loadActualForm={loadActualForm}
        onOpenFullNamePopUp={handleOpenFullNamePopUp}
        isShowSettingsModal={isShowSettingsModal}
      />
      <OAPFooter
        isCurrentPage={isCurrentPage}
        setIsCurrentPage={setIsCurrentPage}
        isSelectPagination={isSelectPagination}
        setIsSelectPagination={setIsSelectPagination}
        actualQuantityRows={actualQuantityRows}
      />
    </div>
  );
};

export default OAPLayout;
